import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Search, Filter, Edit3, X, Settings, Clock, Star, Eye, Trash2, Plus } from 'lucide-react';
import './OwnerMenuManagement.css';
import '../TodaysSalesReport.css'; // Import TSR styles for substitute modal
import InventoryService from '../../services/inventoryService';

// Import API_BASE_URL
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL;

// Searchable Select Component (from TSR)
const SearchableSelect = ({ value, onChange, options = [], placeholder = 'Search unit...', disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [openUpward, setOpenUpward] = useState(false);
  const wrapRef = useRef(null);
  const controlRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (open && controlRef.current) {
      const rect = controlRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 250;
      
      setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
    }
  }, [open]);

  const selected = options.find(o => o.value === value);
  const filtered = options.filter(o =>
    (o.label || o.value || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div ref={wrapRef} className="substitute-searchable-select">
      <div
        ref={controlRef}
        className="substitute-searchable-control"
        onClick={() => !disabled && setOpen(!open)}
      >
        <span>{selected ? (selected.label || selected.value) : placeholder}</span>
        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
      {open && (
        <div className={`substitute-searchable-menu ${openUpward ? 'upward' : ''}`}>
          <input
            type="text"
            placeholder="Search unit..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="substitute-searchable-input"
            autoFocus
          />
          <div className="substitute-searchable-options">
            {filtered.map((opt) => (
              <div
                key={opt.value}
                className={`substitute-searchable-option ${opt.value === value ? 'selected' : ''}`}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                  setQuery('');
                }}
              >
                {opt.label || opt.value}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="substitute-searchable-option disabled">No results</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const OwnerMenuManagement = ({ onBack, goTo }) => {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [category, setCategory] = useState('All');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const selectedItemRef = useRef(null);
  const [editingName, setEditingName] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [itemToEnable, setItemToEnable] = useState(null);
  const [newCustName, setNewCustName] = useState('');
  const [addingCust, setAddingCust] = useState(false);
  const [hasUnsavedCustomizations, setHasUnsavedCustomizations] = useState(false);
  const [editingPrepTime, setEditingPrepTime] = useState('');
  const [editingTrending, setEditingTrending] = useState(false);
  const [editingHasOffer, setEditingHasOffer] = useState(false);
  const [editingOfferPercentage, setEditingOfferPercentage] = useState(10);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [ingredientsList, setIngredientsList] = useState([]);
  const [loadingIngredients, setLoadingIngredients] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [pendingSaveData, setPendingSaveData] = useState(null);
  
  // Image editing state
  const [editingImageFile, setEditingImageFile] = useState(null);
  const [editingImagePreview, setEditingImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);
  
  // Missing ingredients checkbox selection state
  const [selectedMissingIngredients, setSelectedMissingIngredients] = useState({});
  
  // ✅ Track items that were manually toggled by owner (PERSISTED across refreshes)
  const [forceEnabledItems, setForceEnabledItems] = useState(() => {
    try {
      const stored = localStorage.getItem('ownerForceEnabledItems');
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(parsed);
      }
    } catch (error) {
      console.error('Error loading force-enabled items:', error);
    }
    return new Set();
  });
  
  const [forceDisabledItems, setForceDisabledItems] = useState(() => {
    try {
      const stored = localStorage.getItem('ownerForceDisabledItems');
      if (stored) {
        const parsed = JSON.parse(stored);
        return new Set(parsed);
      }
    } catch (error) {
      console.error('Error loading force-disabled items:', error);
    }
    return new Set();
  });
  
  const forceEnabledItemsRef = useRef(new Set());
  const forceDisabledItemsRef = useRef(new Set());
  
  // Keep refs in sync with state AND save to localStorage
  useEffect(() => {
    forceEnabledItemsRef.current = forceEnabledItems;
    try {
      const itemsArray = Array.from(forceEnabledItems);
      localStorage.setItem('ownerForceEnabledItems', JSON.stringify(itemsArray));
    } catch (error) {
      console.error('Error saving force-enabled items:', error);
    }
  }, [forceEnabledItems]);
  
  useEffect(() => {
    forceDisabledItemsRef.current = forceDisabledItems;
    try {
      const itemsArray = Array.from(forceDisabledItems);
      localStorage.setItem('ownerForceDisabledItems', JSON.stringify(itemsArray));
    } catch (error) {
      console.error('Error saving force-disabled items:', error);
    }
  }, [forceDisabledItems]);
  
  // Substitute modal state
  const [showSubstituteModal, setShowSubstituteModal] = useState(false);
  const [substituteData, setSubstituteData] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [substituteSearchText, setSubstituteSearchText] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  
  // Track resolved ingredients (which ones have been handled via negative stock, substitute, or recipe removal)
  const [resolvedIngredients, setResolvedIngredients] = useState(new Set());

  // Fetch menu items from backend on mount
  useEffect(() => {
    loadMenuItems();
  }, []);
  
  // Check if returning from Item Mapping after recipe update
  useEffect(() => {
    const checkReturnFromItemMapping = async () => {
      const pendingData = localStorage.getItem('pendingMenuManagementData');
      if (pendingData) {
        try {
          const data = JSON.parse(pendingData);
          
          // Clear the localStorage flag
          localStorage.removeItem('pendingMenuManagementData');
          localStorage.removeItem('cameFromMenuManagement');
          localStorage.removeItem('autoSelectRecipeId');
          
          // Restore resolved ingredients
          if (data.resolvedIngredients && Array.isArray(data.resolvedIngredients)) {
            setResolvedIngredients(new Set(data.resolvedIngredients));
          }
          
          // Find the menu item that was being edited
          const itemId = data.menuItemId;
          const item = menuItems.find(mi => (mi.menu_item_id || mi.id) === itemId);
          
          if (item) {
            
            // Re-fetch ingredients to see if they're still missing
            await fetchIngredients(itemId);
            
            // Check if ingredients were already loaded
            let currentIngredients = ingredientsList;
            
            // If ingredients aren't loaded yet, fetch them
            if (!currentIngredients || currentIngredients.length === 0) {
              try {
                const businessId = Number(localStorage.getItem('businessId'));
                const response = await fetch(`${API_BASE_URL}/recipes/${itemId}/ingredients?businessId=${businessId}`);
                if (response.ok) {
                  const ingredientsData = await response.json();
                  currentIngredients = ingredientsData;
                  setIngredientsList(ingredientsData);
                }
              } catch (fetchErr) {
                console.error('Error fetching ingredients:', fetchErr);
                currentIngredients = [];
              }
            }
            
            // Count remaining missing ingredients
            const remainingMissing = (currentIngredients || []).filter(ing => {
              const key = ing.ingredient_name || ing.name;
              const isOutOfStock = ing.is_out_of_stock || ing.current_stock === 0;
              const isResolved = (data.resolvedIngredients || []).includes(key);
              return isOutOfStock && !isResolved;
            }).length;
            

            // ✅ If no missing ingredients OR this is a newly created item (no ingredients yet), auto-enable
            if (remainingMissing === 0 || currentIngredients.length === 0) {

              // Enable the item immediately
              try {
                const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${itemId}/availability`, {
                  method: 'PUT',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ is_available: true })
                });
                
                if (response.ok) {
                  // Add to force-enabled items to ensure it stays enabled
                  setForceEnabledItems(prev => new Set([...prev, String(itemId)]));
                  showSuccess(`✅ Recipe updated! "${item.name}" is now enabled.`);
                  await loadMenuItems(); // Refresh menu items
                  setResolvedIngredients(new Set());
                } else {
                  throw new Error('Failed to enable item');
                }
              } catch (err) {
                console.error('Error enabling item:', err);
                setError('Failed to enable item after recipe update');
              }
            } else {
              // Still have missing ingredients, show the modal
              setItemToEnable(item);
              setShowEnableModal(true);
              setError(`Recipe updated. ${remainingMissing} ingredient(s) still need handling.`);
            }
          }
        } catch (err) {
          console.error('Error restoring from Item Mapping:', err);
        }
      }
    };
    
    if (menuItems.length > 0) {
      checkReturnFromItemMapping();
    }
  }, [menuItems]);

  // Load inventory items when substitute modal opens
  useEffect(() => {
    if (showSubstituteModal && inventoryItems.length === 0) {
      (async () => {
        try {
          const businessId = Number(localStorage.getItem('businessId'));
          const response = await InventoryService.getInventoryItems(businessId, 1000);
          if (response?.data) {
            setInventoryItems(response.data);
          }
        } catch (err) {
          console.error('useEffect: Failed to load inventory:', err);
        }
      })();
    }
  }, [showSubstituteModal, inventoryItems.length]);

  // Keep ref in sync with selectedItem state
  useEffect(() => {
    selectedItemRef.current = selectedItem;
  }, [selectedItem]);

  const loadMenuItems = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      // ✅ FIXED: Include inactive items so newly onboarded items appear (will be shown as disabled)
      const response = await fetch(`${API_BASE_URL}/menu/items?includeInactive=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch menu items');
      }

      const result = await response.json();
      
      
      if (result.success && Array.isArray(result.data)) {
        // 🔍 DEBUG: Log raw backend data for ABC stock checks
        
        // Transform backend data to match component structure
        const transformedItems = result.data.map(item => {
          // ✅ Helper: Parse PostgreSQL array format to JavaScript array
          const parsePostgresArray = (value) => {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            if (typeof value === 'string') {
              // PostgreSQL arrays come as "{A,B,C}" or empty "{}"
              if (value.startsWith('{') && value.endsWith('}')) {
                const content = value.slice(1, -1);
                return content ? content.split(',').map(s => s.trim()) : [];
              }
              return [value]; // Single value
            }
            return [];
          };

          const transformed = {
            id: item.id.toString(),
            name: item.name,
            category: item.category || 'Uncategorized',
            price: parseFloat(item.price) || 0,
            prepTime: item.avg_prep_time_minutes || item.prepTime || null,
            description: item.description || '',
            image_url: item.img || item.image_url || item.fallback_img || '',
            customizations: item.customizations || [],
            // ✅ CRITICAL: Preserve exact backend state
            is_disabled: Boolean(item.is_disabled),
            is_active: Boolean(item.is_active),
            // is_active now means only "the dish exists". Whether it can be ordered is
            // is_disabled (owner switched it off) or stock_unavailable (ingredients ran out).
            stock_unavailable: Boolean(item.stock_unavailable),
            isAvailable: Boolean(item.is_active) && !Boolean(item.is_disabled) && !Boolean(item.stock_unavailable),
            disabled_reason: item.disabled_reason || item.stock_unavailable_reason || '',
            // ✅ NEW: Available plates calculation
            // NULL means all ingredients force-enabled (negative stock), so don't limit plates
            available_plates: item.available_plates !== null && item.available_plates !== undefined 
              ? parseInt(item.available_plates) 
              : null, // null = unlimited (force-enabled with negative stock)
            // ✅ NEW: Use ABC category low stock check with proper array parsing
            has_low_stock_ingredients: item.has_low_stock_abc_ingredients || false,
            low_stock_ingredient_names: parsePostgresArray(item.low_stock_abc_ingredient_names),
            missing_abc_categories: parsePostgresArray(item.missing_abc_categories),
            // ✅ NEW: All low stock ingredients (for 0 plates display)
            all_low_stock_ingredients: parsePostgresArray(item.all_low_stock_ingredients),
            // Legacy: Still keep general missing ingredients check for reference
            has_missing_a_ingredients: item.has_missing_ingredients || false,
            missing_ingredient_names: parsePostgresArray(item.missing_ingredient_names)
          };
          
          
          return transformed;
        });

        // ✅ Apply manual override states AFTER transformation (persisted across refreshes)
        const transformedWithOverrides = transformedItems.map(item => {
          const itemIdStr = String(item.id);
          const isForceEnabled = forceEnabledItemsRef.current.has(itemIdStr);
          const isForceDisabled = forceDisabledItemsRef.current.has(itemIdStr);
          
          // ✅ PRIORITY 1: If manually force-disabled, always show as disabled
          if (isForceDisabled) {
            return {
              ...item,
              isAvailable: false,
              is_disabled: true,
              // is_active stays true: switching a dish off is not deleting it.
              is_active: true,
              disabled_reason: 'Item deactivated by owner'
            };
          }
          
          // ✅ PRIORITY 2: If manually force-enabled, always show as enabled
          if (isForceEnabled) {
            return {
              ...item,
              isAvailable: true,
              is_disabled: false,
              is_active: true,
              stock_unavailable: false,
              disabled_reason: '',
              // Clear ALL ABC stock flags
              has_low_stock_ingredients: false,
              has_missing_a_ingredients: false,
              missing_ingredient_names: [],
              low_stock_ingredient_names: [],
              missing_abc_categories: [],
              all_low_stock_ingredients: []
              // ✅ Keep available_plates from backend data
            };
          }
          
          // ✅ PRIORITY 3: No manual override, use backend state
          return item;
        });

        setMenuItems(transformedWithOverrides);

        // Extract unique categories
        const uniqueCategories = ['All', ...new Set(transformedWithOverrides.map(item => item.category).filter(Boolean))];
        setCategories(uniqueCategories);

        // 🔍 DEBUG: Summary of ABC low stock items
        const lowStockCount = transformedWithOverrides.filter(i => i.has_low_stock_ingredients).length;
        if (lowStockCount > 0) {
          console.log('🔴 Low stock items:', transformedWithOverrides.filter(i => i.has_low_stock_ingredients).map(i => i.name));
        }
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error loading menu items:', err);
      setError(err.message || 'Failed to load menu items');
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Success notification popup disabled
  const showSuccess = (message) => {
    // Function disabled to remove popup notifications
  };

  const clearError = () => {
    setError('');
  };

  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    
    setEditingImageFile(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditingImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
    
    // Mark as having unsaved changes
    setHasUnsavedCustomizations(true);
  };
  
  const handleRemoveImage = () => {
    setEditingImageFile(null);
    setEditingImagePreview(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
    setHasUnsavedCustomizations(true);
  };

  const handleSaveEdit = async () => {
    // Use ref to get the LATEST selectedItem (avoids closure issues)
    const currentItem = selectedItemRef.current;
    
    if (!currentItem) {
      console.error('❌ [SAVE] currentItem is null!');
      return;
    }

    try {
      
      // Validate prep time
      const prepTime = parseInt(editingPrepTime, 10);
      if (editingPrepTime && (!Number.isFinite(prepTime) || prepTime < 0 || prepTime > 240)) {
        setError('Prep time must be between 0 and 240 minutes');
        return;
      }
      
      // Upload image first if a new image was selected
      let imageUrl = currentItem.image_url;
      if (editingImageFile) {
        setUploadingImage(true);
        
        try {
          const formData = new FormData();
          formData.append('image', editingImageFile);
          formData.append('menuItemId', currentItem.id);
          
          const token = localStorage.getItem('token');
          const businessId = localStorage.getItem('businessId');
          const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${currentItem.id}/upload-image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-business-id': businessId
            },
            body: formData
          });
          
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to upload image');
          }
          
          const result = await response.json();
          imageUrl = result.imageUrl;
        } catch (uploadError) {
          console.error('❌ Image upload error:', uploadError);
          setError(uploadError.message || 'Failed to upload image');
          setUploadingImage(false);
          return;
        } finally {
          setUploadingImage(false);
        }
      }
      
      // Prepare update payload
      const updates = {
        name: editingName || currentItem.name,
        prepTime: prepTime || null,
        description: editingDescription || currentItem.description,
        customizations: currentItem.customizations || [],
        is_trending: editingTrending,
        has_offer: editingHasOffer,
        offer_percentage: editingHasOffer ? (editingOfferPercentage || 0) : null,
        image_url: imageUrl
      };
      
      // Check if trending or offer changed - if yes, show confirmation
      const trendingChanged = editingTrending !== (currentItem.is_trending || false);
      const offerChanged = editingHasOffer !== (currentItem.has_offer || false);
      const offerPercentageChanged = editingHasOffer && currentItem.has_offer && (editingOfferPercentage || 0) !== (currentItem.offer_percentage || 0);
      
      if (trendingChanged || offerChanged || offerPercentageChanged) {
        // Generate confirmation message
        let message = '';
        const itemName = currentItem.name;
        const originalPrice = currentItem.price;
        const percentageToUse = editingOfferPercentage || 0;
        const discountedPrice = editingHasOffer 
          ? (originalPrice * (1 - percentageToUse / 100)).toFixed(2)
          : originalPrice.toFixed(2);
        
        if (offerChanged || offerPercentageChanged) {
          if (editingHasOffer) {
            // Offer is being enabled
            message = `• Applying a ${percentageToUse}% promotional offer to ${itemName}.\n`;
            message += `• Price updating from ₹${originalPrice.toFixed(2)} to ₹${discountedPrice}.\n`;
            message += `• No changes made to the Trending list.`;
            
            if (trendingChanged) {
              if (editingTrending) {
                // Scenario 1: Enabling Offer + Adding to Trending
                message = `• Applying a ${percentageToUse}% promotional offer to ${itemName}.\n`;
                message += `• Price updating from ₹${originalPrice.toFixed(2)} to ₹${discountedPrice}.\n`;
                message += `• This item will also be listed in the Trending section.`;
              } else {
                // Scenario 2: Enabling Offer + Removing from Trending
                message = `• Applying a ${percentageToUse}% promotional offer to ${itemName}.\n`;
                message += `• Price updating from ₹${originalPrice.toFixed(2)} to ₹${discountedPrice}.\n`;
                message += `• This item will be removed from the Trending list.`;
              }
            }
          } else {
            // Offer is being removed
            message = `• Removing the promotional offer from ${itemName}.\n`;
            message += `• Price reverting to ₹${originalPrice.toFixed(2)}.\n`;
            
            if (trendingChanged) {
              if (editingTrending) {
                message += `• This item will also be listed in the Trending section.`;
              } else {
                message += `• This item will be removed from the Trending list.`;
              }
            } else {
              message += `• No changes made to the Trending list.`;
            }
          }
        } else if (trendingChanged) {
          // Only trending changed, no offer changes
          if (editingTrending) {
            message = `• ${itemName} will be added to the Trending section.`;
          } else {
            message = `• ${itemName} will be removed from the Trending list.`;
          }
        }
        
        setConfirmationMessage(message);
        setPendingSaveData(updates);
        setShowConfirmationModal(true);
        return; // Don't proceed with save yet
      }
      
      // No trending/offer changes, proceed directly
      await proceedWithSave(updates);
      
    } catch (error) {
      console.error('❌ Error in handleSaveEdit:', error);
      setError(error?.message || 'Failed to prepare update');
    }
  };

  const proceedWithSave = async (updates) => {
    const currentItem = selectedItemRef.current;
    if (!currentItem) return;
    
    try {
      setSaving(true);
      
      // Call backend API to save menu item
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${currentItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update menu item');
      }

      const result = await response.json();
      
      // Also update recipe description and customizations
      try {
        // Extract add-ons from customizations
        // Customizations are stored as { name: 'Options', type: 'checkbox', options: [{name: 'Ghee'}, {name: 'Butter'}] }
        // We need to extract the option names into a flat array for add_ons
        const optionsCustomization = (updates.customizations || []).find(c => c.name === 'Options');
        const addOns = optionsCustomization?.options?.map(opt => opt.name) || [];
        
        const complimentaryItems = (updates.customizations || [])
          .filter(c => c.type === 'complimentary')
          .map(c => c.name);
        
        // TWO endpoints, because they own different columns:
        //   PUT /recipes/:id            → description (and name/price/servings)
        //   PUT /recipes/:id/customizations → add_ons + complimentary_items (junction tables)
        // Sending all four to the first one — as this did — saved the description and silently
        // discarded the customizations, while the response was still ok and the log still said
        // "description and customizations updated successfully".
        const authHeaders = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        };

        const [recipeResponse, customizationsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/recipes/${currentItem.id}`, {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({ description: updates.description })
          }),
          fetch(`${API_BASE_URL}/recipes/${currentItem.id}/customizations`, {
            method: 'PUT',
            headers: authHeaders,
            body: JSON.stringify({ add_ons: addOns, complimentary_items: complimentaryItems })
          })
        ]);

        if (recipeResponse.ok && customizationsResponse.ok) {
          console.log('✅ Recipe description and customizations updated successfully');
        } else {
          // Reported separately: "customizations did not save" is a different problem from
          // "the description did not save", and one message for both hides which happened.
          if (!recipeResponse.ok) console.warn('⚠️ Failed to update recipe description, but menu item was saved');
          if (!customizationsResponse.ok) console.warn('⚠️ Failed to update recipe customizations, but menu item was saved');
        }
      } catch (recipeError) {
        console.error('❌ Error updating recipe:', recipeError);
        // Continue even if recipe update fails
      }
      
      // Update local state with ALL the changes
      setMenuItems(prev => (Array.isArray(prev) ? prev : []).map(item => 
        item.id === currentItem.id 
          ? { 
              ...item, 
              name: updates.name,
              prepTime: updates.prepTime,
              description: updates.description,
              customizations: updates.customizations,
              is_trending: updates.is_trending,
              has_offer: updates.has_offer,
              offer_percentage: updates.offer_percentage,
              image_url: updates.image_url
            }
          : item
      ));
      
      setShowEditModal(false);
      setShowConfirmationModal(false);
      setPendingSaveData(null);
      setHasUnsavedCustomizations(false);
      
      // Show success popup
      setShowSuccessPopup(true);
      setTimeout(() => setShowSuccessPopup(false), 2000);
      
    } catch (error) {
      console.error('❌ Error updating item:', error);
      setError(error?.message || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSave = () => {
    if (pendingSaveData) {
      proceedWithSave(pendingSaveData);
    }
  };

  const handleCancelSave = () => {
    setShowConfirmationModal(false);
    setPendingSaveData(null);
    setConfirmationMessage('');
  };

  const handleEditItem = async (item) => {
    
    setSelectedItem(item);
    setEditingName(item.name);
    setEditingPrepTime(String(item.prepTime ?? ''));
    setEditingTrending(item.is_trending || false);
    setEditingHasOffer(item.has_offer || false);
    setEditingOfferPercentage(item.offer_percentage || 10);
    
    // Reset image editing state
    setEditingImageFile(null);
    setEditingImagePreview(null);
    
    // Fetch description and customizations from recipe/ingredient mapping
    try {
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      const response = await fetch(`${API_BASE_URL}/recipes/${item.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Set description from recipe if available
        if (data.data?.description) {
          setEditingDescription(data.data.description);
        } else {
          setEditingDescription(item.description || '');
        }
        
        // Update customizations from recipe
        if (data.data?.add_ons || data.data?.complimentary_items) {
          const customizations = [];
          
          // Transform add_ons array into Options customization object
          if (data.data.add_ons && Array.isArray(data.data.add_ons) && data.data.add_ons.length > 0) {
            const optionsCustomization = {
              id: Date.now(),
              name: 'Options',
              type: 'checkbox',
              options: data.data.add_ons.map(addon => {
                // Handle both string and object formats
                const addonName = typeof addon === 'string' ? addon : (addon?.name || 'Unknown');
                return { name: addonName, price: 0 };
              }),
              required: false
            };
            customizations.push(optionsCustomization);
          }
          
          // Transform complimentary_items
          if (data.data.complimentary_items && Array.isArray(data.data.complimentary_items)) {
            customizations.push(...data.data.complimentary_items.map(comp => {
              // Handle both string and object formats
              const compName = typeof comp === 'string' ? comp : (comp?.name || 'Unknown');
              return { name: compName, type: 'complimentary' };
            }));
          }
          
          const updatedItem = {
            ...item,
            customizations: customizations
          };
          
          
          setSelectedItem(updatedItem);
          selectedItemRef.current = updatedItem;
        } else {
          // No add_ons or complimentary_items from recipe, keep item as is
          setSelectedItem(item);
          selectedItemRef.current = item;
        }
      } else {
        // Fallback to item description if API call fails
        setEditingDescription(item.description || '');
      }
    } catch (error) {
      console.error('❌ Error fetching recipe details:', error);
      // Fallback to item description
      setEditingDescription(item.description || '');
    }
    
    setShowEditModal(true);
  };

  const handleDisabledItemClick = async (item) => {
    
    // Fetch ingredients for this menu item
    await fetchIngredients(item.id);
    
    // Show custom enable confirmation modal instead of browser confirm
    setItemToEnable(item);
    setShowEnableModal(true);
  };

  const fetchIngredients = async (menuItemId) => {
    try {
      setLoadingIngredients(true);
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      const response = await fetch(`${API_BASE_URL}/recipes/${menuItemId}/ingredients`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch ingredients');
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        
        // ✅ FIXED: Show ALL ingredients with low/zero stock, not just ABC categories
        // This is important for newly onboarded businesses where ABC analysis hasn't run yet
        const allIngredients = result.data.filter(ing => {
          const stock = parseFloat(ing.current_stock || 0);
          const isLowStock = stock <= 0 || ing.is_in_stock === false;
          return isLowStock; // Show all ingredients that are out of stock
        });
        
        
        // If no low stock ingredients found, show all ingredients (for debugging)
        const ingredientsToShow = allIngredients.length > 0 ? allIngredients : result.data;
        
        // Step 2: Deduplicate ingredients by grouping by ingredient name
        const ingredientMap = new Map();
        
        ingredientsToShow.forEach(ing => {
          const key = ing.ingredient_name; // Group by name
          
          if (ingredientMap.has(key)) {
            // Ingredient already exists, sum the quantities
            const existing = ingredientMap.get(key);
            existing.quantity = parseFloat(existing.quantity) + parseFloat(ing.quantity);
            existing.total_cost = existing.quantity * parseFloat(existing.unit_cost || 0);
          } else {
            // New ingredient, add to map
            ingredientMap.set(key, {
              ...ing,
              quantity: parseFloat(ing.quantity),
              unit_cost: parseFloat(ing.unit_cost || 0),
              total_cost: parseFloat(ing.quantity) * parseFloat(ing.unit_cost || 0),
              current_stock: parseFloat(ing.current_stock || 0),
              is_out_of_stock: parseFloat(ing.current_stock || 0) <= 0 // Mark if out of stock
            });
          }
        });
        
        // Step 3: Convert map back to array (deduplicated)
        const deduplicatedIngredients = Array.from(ingredientMap.values());
        
        // Step 4: Set the ingredients list
        setIngredientsList(deduplicatedIngredients);
        
        // ✅ AUTO-SELECT all out-of-stock ingredients
        const autoSelected = {};
        deduplicatedIngredients.forEach(ing => {
          if (ing.is_out_of_stock) {
            const ingredientKey = ing.ingredient_name || ing.name;
            autoSelected[ingredientKey] = true;
          }
        });
        setSelectedMissingIngredients(autoSelected);
        
      } else {
        setIngredientsList([]);
      }
    } catch (error) {
      console.error('Error fetching ingredients:', error);
      setIngredientsList([]);
    } finally {
      setLoadingIngredients(false);
    }
  };

  const confirmEnableItem = async () => {
    if (!itemToEnable) return;
    
    // Store item info before clearing state
    const itemId = itemToEnable.id;
    const itemName = itemToEnable.name;
    
    // Get all selected ingredients that need negative stock
    const selectedIngredientsForNegativeStock = Object.keys(selectedMissingIngredients)
      .filter(key => selectedMissingIngredients[key])
      .map(key => key); // These are ingredient names
    
    try {
      setSaving(true);
      
      // Call backend API to enable the item with selected ingredients
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${itemId}/availability`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        },
        body: JSON.stringify({ 
          isAvailable: true,
          selectedIngredients: selectedIngredientsForNegativeStock // Pass selected ingredients
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to enable item');
      }

      const result = await response.json();
      
      // ✅ Mark this item as force-enabled and remove from force-disabled
      const itemIdStr = String(itemId);
      setForceEnabledItems(prev => {
        const newSet = new Set([...prev, itemIdStr]);
        return newSet;
      });
      setForceDisabledItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemIdStr);
        return newSet;
      });
      
      // ✅ CRITICAL FIX: Immediately update local state to remove gray mode
      // This ensures the item shows as enabled right away
      setMenuItems(prev => (Array.isArray(prev) ? prev : []).map(it => 
        it.id === itemId 
          ? { 
              ...it, 
              isAvailable: true,
              is_disabled: false,
              is_active: true,
              stock_unavailable: false,
              disabled_reason: '',
              // ✅ Clear ALL ABC stock flags that cause gray mode
              has_low_stock_ingredients: false,
              has_missing_a_ingredients: false,
              missing_ingredient_names: [],
              low_stock_ingredient_names: [],
              missing_abc_categories: [],
              all_low_stock_ingredients: []
              // ✅ Keep available_plates from backend - will be refreshed on reload
            }
          : it
      ));
      
      // Show success message immediately
      showSuccess(`✅ "${itemName}" is now enabled and visible to customers!`);
      
      // ✅ Reload menu items to sync with backend
      // The forceEnabledItemsRef will ensure it stays enabled during reload
      try {
        await loadMenuItems();
      } catch (reloadError) {
        console.error('❌ Error reloading menu items (non-critical):', reloadError);
      }
      
      // Close modal AFTER reload
      setShowEnableModal(false);
      setItemToEnable(null);
      setIngredientsList([]);
      setSelectedMissingIngredients({}); // Clear selections
      setResolvedIngredients(new Set()); // Clear resolved set
      
    } catch (error) {
      console.error('❌ Error enabling item:', error);
      setError(error?.message || 'Failed to enable item');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      setSaving(true);
      const newStatus = !item.isAvailable;
      
      // ✅ Call backend API to update database
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');
      const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${item.id}/availability`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        },
        body: JSON.stringify({ isAvailable: newStatus })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update item availability');
      }

      const result = await response.json();
      
      // ✅ Track if enabling or disabling
      const itemIdStr = String(item.id);
      if (newStatus) {
        // Enabling - add to force-enabled set, remove from force-disabled
        setForceEnabledItems(prev => {
          const newSet = new Set([...prev, itemIdStr]);
          return newSet;
        });
        setForceDisabledItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemIdStr);
          return newSet;
        });
      } else {
        // Disabling - add to force-disabled set, remove from force-enabled
        setForceDisabledItems(prev => {
          const newSet = new Set([...prev, itemIdStr]);
          return newSet;
        });
        setForceEnabledItems(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemIdStr);
          return newSet;
        });
      }
      
      // ✅ CRITICAL FIX: Update local state with ALL necessary flags cleared when enabling
      setMenuItems(prev => (Array.isArray(prev) ? prev : []).map(it => 
        it.id === item.id 
          ? { 
              ...it, 
              isAvailable: newStatus,
              is_disabled: !newStatus,
              is_active: newStatus,
              disabled_reason: !newStatus ? 'Item deactivated by owner' : '',
              // ✅ When enabling, clear ALL ABC stock flags that cause gray mode
              ...(newStatus ? {
                has_low_stock_ingredients: false,
                has_missing_a_ingredients: false,
                missing_ingredient_names: [],
                low_stock_ingredient_names: [],
                missing_abc_categories: [],
                all_low_stock_ingredients: []
                // ✅ Keep available_plates from backend data
              } : {})
            }
          : it
      ));
      
      // ✅ CRITICAL FIX: Update selectedItem immediately with all flags
      if (selectedItem && selectedItem.id === item.id) {
        setSelectedItem(prev => ({
          ...prev,
          isAvailable: newStatus,
          is_disabled: !newStatus,
          // The dish keeps existing either way; only its availability changes.
          is_active: true,
          stock_unavailable: newStatus ? false : prev.stock_unavailable,
          disabled_reason: !newStatus ? 'Item deactivated by owner' : '',
          // ✅ When enabling, clear ALL ABC stock flags
          ...(newStatus ? {
            has_low_stock_ingredients: false,
            has_missing_a_ingredients: false,
            missing_ingredient_names: [],
            low_stock_ingredient_names: [],
            missing_abc_categories: [],
            all_low_stock_ingredients: []
            // ✅ Keep available_plates from backend data
          } : {})
        }));
      }
      
      showSuccess(`Item ${newStatus ? 'enabled' : 'disabled'} successfully!`);
      
    } catch (error) {
      console.error('❌ Error toggling availability:', error);
      setError(error?.message || 'Failed to update item availability');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTiming = async () => {
    if (!selectedItem) return;
    const minutes = parseInt(editingPrepTime, 10);
    if (!Number.isFinite(minutes) || minutes < 0 || minutes > 240) {
      setError('Prep time must be between 0 and 240 minutes');
      return;
    }
    try {
      setSaving(true);
      
      // Mock save - just update local state
      
      // Update local state
      setSelectedItem(prev => ({ ...prev, prepTime: minutes }));
      setMenuItems(prev => (Array.isArray(prev) ? prev : []).map(it => 
        it.id === selectedItem.id ? { ...it, prepTime: minutes } : it
      ));
      
      showSuccess('Prep time updated successfully!');
      
    } catch (error) {
      console.error('❌ Error updating timing:', error);
      setError(error?.message || 'Failed to update timing');
    } finally {
      setSaving(false);
    }
  };

  // Filter items based on search and category (guard against undefined)
  const safeMenuItems = Array.isArray(menuItems) ? menuItems : [];
  const filteredItems = safeMenuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchText.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchText.toLowerCase());
    const matchesCategory = category === 'All' || item.category === category;
    return matchesSearch && matchesCategory;
  });

  const handleRemoveCustomization = (index) => {
    if (!selectedItem) return;
    
    try {
      
      const existingOptions = Array.isArray(selectedItem?.customizations)
        ? selectedItem.customizations.find(c => c.name === 'Options')
        : undefined;
      
      if (existingOptions && Array.isArray(existingOptions.options)) {
        // Remove the option at the specified index
        const updatedOptions = [...existingOptions.options];
        updatedOptions.splice(index, 1);
        
        const updatedCustomization = {
          ...existingOptions,
          options: updatedOptions
        };
        
        const updatedSelectedItem = {
          ...selectedItem,
          customizations: selectedItem.customizations.map(c => 
            c.name === 'Options' ? updatedCustomization : c
          )
        };
        
        
        setSelectedItem(updatedSelectedItem);
        setMenuItems(prev => prev.map(it =>
          it.id === selectedItem.id ? updatedSelectedItem : it
        ));
        
        // Mark that customizations have been modified
        setHasUnsavedCustomizations(true);
      }
    } catch (error) {
      console.error('❌ Error removing customization option:', error);
    }
  };

  const handleAddCustomization = async () => {
    if (!selectedItem || !newCustName.trim()) return;
    
    try {
      setAddingCust(true);
      
      // Update local state ONLY - actual save happens when clicking "Save Changes"
      const existingOptions = Array.isArray(selectedItem?.customizations)
        ? selectedItem.customizations.find(c => c.name === 'Options')
        : undefined;
      
      let updatedSelectedItem;
      if (existingOptions) {
        // Add to existing options
        const newOption = { name: newCustName.trim(), price: 0 };
        const updatedCustomization = {
          ...existingOptions,
          options: [...(existingOptions.options || []), newOption]
        };
        updatedSelectedItem = {
          ...selectedItem,
          customizations: selectedItem.customizations.map(c => 
            c.name === 'Options' ? updatedCustomization : c
          )
        };
      } else {
        // Create new Options customization
        const newOption = { name: newCustName.trim(), price: 0 };
        const newCustomization = { 
          id: Date.now(), 
          name: 'Options', 
          type: 'checkbox', 
          options: [newOption],
          required: false
        };
        updatedSelectedItem = {
          ...selectedItem,
          customizations: Array.isArray(selectedItem?.customizations) 
            ? [...selectedItem.customizations, newCustomization] 
            : [newCustomization]
        };
      }
      
      
      setSelectedItem(updatedSelectedItem);
      selectedItemRef.current = updatedSelectedItem; // Update ref as well!
      setMenuItems(prev => prev.map(it =>
        it.id === selectedItem.id ? updatedSelectedItem : it
      ));
      
      // Mark that customizations have been modified
      setHasUnsavedCustomizations(true);
      
      // Reset form
      setNewCustName('');
      
      // Verify state update in next tick
      setTimeout(() => {
        console.log('⏱️  After state update - selectedItem.customizations:', selectedItem.customizations);
      }, 100);
      
      showSuccess(`Added "${newCustName.trim()}" option! Click "Save Changes" to persist.`);
      
    } catch (error) {
      console.error('❌ Error adding customization:', error);
      setError(error?.message || 'Failed to add option');
    } finally {
      setAddingCust(false);
    }
  };

  if (loading) {
    return (
      <div className="owner-menu-container">
        <div className="owner-loading-state">
          <div className="owner-loading-spinner"></div>
          <h3>Loading Menu Items...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="owner-menu-container">
        <div className="owner-error-state">
          <X size={48} color="#ff4444" />
          <h3>Unable to Load Menu</h3>
          <p>{error}</p>
          <button onClick={loadMenuItems} className="owner-retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="owner-menu-container">
      {/* Success Message popup removed */}

      {/* Error Message */}
      {error && (
        <div className="owner-error-message">
          <span>❌ {error}</span>
          <button onClick={clearError} className="owner-error-dismiss">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Header - Simplified */}
      <div className="owner-menu-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
          <button className="owner-back-btn" onClick={onBack} title="Back">
            <ArrowLeft size={24} />
          </button>
          <div className="owner-header-text">
            <h2>Menu Management</h2>
          </div>
        </div>
      </div>

      {/* Search Bar - Exact copy from customer menu */}
      <div className="owner-search-bar">
        <Search size={24} />
        <input 
          value={searchText} 
          onChange={e=>setSearchText(e.target.value)} 
          placeholder="Search for food" 
        />
      </div>

      {/* Categories - Exact copy from customer menu */}
      <div className="owner-categories">
        {categories.map(c => (
          <button 
            key={c} 
            onClick={()=>setCategory(c)} 
            className={"owner-cat" + (category===c ? ' active' : '')}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Menu Grid - Exact copy from customer menu structure */}
      <div className="owner-grid">
        {filteredItems.map(item => {
          // ✅ UPDATED LOGIC FOR ABC CATEGORY LOW STOCK + 0 PLATES:
          // CRITICAL FIX: Respect backend is_active/is_disabled flags FIRST
          // Only use stock-based detection as fallback for automatic disabling
          const isOwnerDisabled = item.is_disabled === true; // Owner manually disabled
          // "Active" here has always meant orderable. It is no longer a single column:
          // the dish must exist AND not be stocked out by the availability service.
          const isBackendActive = item.is_active === true && item.stock_unavailable !== true;
          const hasLowStockABCIngredients = item.has_low_stock_ingredients === true; // System: ABC (A/B/C) low stock
          const hasMissingIngredients = item.has_missing_a_ingredients === true; // Has ANY missing ingredients
          const hasZeroPlates = item.available_plates === 0; // ✅ NEW: No ingredients to make any servings
          const isForceEnabled = item.available_plates === null; // ✅ Force-enabled with negative stock
          
          // ✅ CRITICAL FIX: Gray out items if:
          // 1. Owner manually disabled (isOwnerDisabled) - HIGHEST PRIORITY
          // 2. Backend says inactive (!isBackendActive) BUT owner enabled = DON'T disable
          // 3. Has zero plates BUT owner enabled = DON'T disable (owner override)
          // RULE: If owner enabled (isBackendActive = true), NEVER gray out regardless of stock
          const isDisabled = isOwnerDisabled || (!isBackendActive && hasZeroPlates);
          
          // Show which ABC categories are missing
          const missingCategories = item.missing_abc_categories || [];
          const lowStockIngredients = item.low_stock_abc_ingredient_names || [];
          const allLowStockIngredients = item.all_low_stock_ingredients || []; // ✅ NEW: All missing ingredients
          
          // Build disabled reason message
          let disabledReasonMsg = '';
          let ingredientsToShow = [];
          
          if (hasZeroPlates) {
            disabledReasonMsg = 'Ingredients out of stock';
            ingredientsToShow = allLowStockIngredients; // Show ALL missing ingredients
          } else if (hasLowStockABCIngredients && lowStockIngredients.length > 0) {
            const catText = missingCategories.length > 0 
              ? `${missingCategories.join(', ')}-category` 
              : 'ABC';
            disabledReasonMsg = `Low stock ${catText} ingredients`;
            ingredientsToShow = lowStockIngredients; // Show ABC ingredients
          } else if (isOwnerDisabled) {
            disabledReasonMsg = item.disabled_reason || 'Item disabled';
          }
          
          // ✅ FIX: Can enable if either owner disabled OR has ABC low stock (system disabled) OR has 0 plates
          // Owner should be able to override by restocking or creating negative stock
          const canClickToEnable = isOwnerDisabled || hasLowStockABCIngredients || hasZeroPlates;
          
          return (
            <div key={item.id} className={`owner-item${isDisabled ? ' disabled' : ''}`} style={{ position: 'relative' }}>
              
              <div className="owner-img-wrap" onClick={() => canClickToEnable ? handleDisabledItemClick(item) : (!isDisabled && handleEditItem(item))}>
                <img 
                  src={item.image_url || 'https://images.pexels.com/photos/3026805/pexels-photo-3026805.jpeg?auto=compress&cs=tinysrgb&w=400'} 
                  alt={item.name} 
                  onError={(e) => {e.currentTarget.src = 'https://images.pexels.com/photos/3026805/pexels-photo-3026805.jpeg?auto=compress&cs=tinysrgb&w=400'}}
                />
                {item.prepTime !== null && item.prepTime !== undefined && (
                  <div className="owner-badge time">
                    <Clock size={10} /> {item.prepTime}m
                  </div>
                )}



                {!isDisabled && (
                  <div className="owner-edit-overlay">
                    <Edit3 size={14} /> Edit Item
                  </div>
                )}
              </div>
              <div className="owner-info">
                <h4 onClick={() => {
                  if (canClickToEnable) {
                    handleDisabledItemClick(item);
                  } else if (!isDisabled) {
                    handleEditItem(item);
                  }
                }}>{item.name}</h4>
                
                {/* ✅ NEW: Show available plates - Always show regardless of disabled state */}
                {item.available_plates !== undefined && item.available_plates !== null && item.available_plates < 999999 && (
                  <div style={{ 
                    fontSize: '16px', 
                    color: '#6b7280',
                    fontWeight: 700,
                    marginTop: '8px'
                  }}>
                    est. {item.available_plates > 99 ? '99+' : item.available_plates} {item.available_plates === 1 ? 'plate' : 'plates'}
                  </div>
                )}
                
                {/* ✅ Show "Force Enabled" badge for items with null plates (all ingredients negative stock) */}
                {!isDisabled && item.available_plates === null && (
                  <div style={{ 
                    fontSize: '11px', 
                    color: '#3b82f6',
                    fontWeight: 600,
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span>🔓</span>
                    <span>Force Enabled</span>
                  </div>
                )}
                
                
                {/* Show reason for disabled state - Low Stock Ingredients */}
                {isDisabled && (
                  <div className="owner-disabled-reason">
                    <small>
                      {ingredientsToShow.length > 0 ? (
                        <>
                          <div style={{ fontWeight: 600, color: '#ef4444', marginBottom: '4px' }}>
                            {disabledReasonMsg}:
                          </div>
                          <ul style={{ margin: '4px 0 0 0', paddingLeft: '15px', listStyleType: 'disc', listStylePosition: 'outside' }}>
                            {ingredientsToShow.map((ing, idx) => (
                              <li key={idx} style={{ paddingLeft: '0', marginLeft: '0' }}>{ing}</li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        disabledReasonMsg
                      )}
                    </small>
                  </div>
                )}
                
              </div>
              
              <div className="owner-footer-line">
                <span className="price">₹{item.price}</span>
                <span className="rating">
                  <Star size={12} fill="#fbbf24" color="#fbbf24" /> 4.5
                </span>
              </div>
              
              <div className="owner-actions">
                  <button 
                    className={`owner-edit-btn${isDisabled ? ' disabled-btn-style' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isDisabled) {
                        handleDisabledItemClick(item);
                      } else {
                        handleEditItem(item);
                      }
                    }}
                    disabled={false}
                    title={
                      hasLowStockABCIngredients ? `Disabled - Low stock ${missingCategories.join(', ')}-category ingredients: ${lowStockIngredients.join(', ')}. Restock to enable.` :
                      isOwnerDisabled ? 'Disabled - Click to view ingredients and enable this item' : 
                      'Edit item details'
                    }
                  >
                    <Edit3 size={12} /> {isDisabled ? 'Enable' : 'Edit'}
                  </button>
                  <button 
                    className={`owner-settings-btn${isDisabled ? ' disabled-btn-style' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation(); 
                      if (isDisabled) {
                        handleDisabledItemClick(item);
                      } else {
                        handleEditItem(item);
                      }
                    }}
                    disabled={false}
                    title={
                      hasLowStockABCIngredients ? `Disabled - Low stock ${missingCategories.join(', ')}-category ingredients. Restock to enable.` :
                      isOwnerDisabled ? 'Disabled - Click to view ingredients and enable' :
                      'Settings'
                    }
                  >
                    <Settings size={12} />
                  </button>
                </div>
            </div>
          );
        })}
      </div>

      {/* Edit Modal - Same style as customer menu modals */}
      {showEditModal && selectedItem && (
        <div className="owner-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="owner-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="owner-modal-header">
              <h3>Edit {selectedItem.name}</h3>
              <button onClick={() => setShowEditModal(false)} className="owner-close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="owner-modal-body">
              {/* Image Upload/Edit */}
              <div className="owner-form-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <label style={{ alignSelf: 'flex-start' }}>Item Image</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', width: '100%' }}>
                  {/* Current/Preview Image */}
                  {(editingImagePreview || selectedItem?.image_url) && (
                    <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
                      <img
                        src={editingImagePreview || selectedItem.image_url}
                        alt={selectedItem.name}
                        style={{
                          width: '100%',
                          height: '200px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                          border: '2px solid #e5e7eb'
                        }}
                        onError={(e) => {
                          e.currentTarget.src = 'https://images.pexels.com/photos/3026805/pexels-photo-3026805.jpeg?auto=compress&cs=tinysrgb&w=400';
                        }}
                      />
                      {editingImagePreview && (
                        <button
                          onClick={handleRemoveImage}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '18px',
                            fontWeight: 'bold'
                          }}
                          title="Remove new image"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Upload Button */}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileSelect}
                      style={{ display: 'none' }}
                      id={`image-upload-${selectedItem?.id}`}
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="owner-save-btn"
                      disabled={uploadingImage}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      {uploadingImage ? 'Uploading...' : (editingImagePreview ? 'Change Image' : 'Upload Image')}
                    </button>
                    {editingImageFile && (
                      <span style={{ fontSize: '12px', color: '#10b981' }}>
                        ✓ New image selected
                      </span>
                    )}
                  </div>
                  <small style={{ color: '#999', fontSize: '12px' }}>
                    Upload a new image for this item (max 5MB, JPG/PNG)
                  </small>
                </div>
              </div>

              {/* Item Name */}
              <div className="owner-form-group" style={{ display: 'block' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#F9C806' }}>Item Name</label>
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className="owner-form-control"
                  placeholder="Enter item name"
                  style={{ width: '100%', display: 'block', marginLeft: 0, marginRight: 0 }}
                />
              </div>

              {/* Trending Toggle */}
              <div className="owner-form-group" style={{ paddingTop: '12px', paddingBottom: '12px', paddingLeft: '16px', paddingRight: '16px', display: 'flex', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#f9c806' }}>Trending</span>
                    <div 
                      className={`owner-toggle-switch ${editingTrending ? 'active' : ''}`}
                      onClick={() => setEditingTrending(!editingTrending)}
                      style={{
                        width: '50px',
                        height: '26px',
                        backgroundColor: editingTrending ? '#fbbf24' : '#4b5563',
                        borderRadius: '13px',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s',
                        marginTop: '10px'
                      }}
                    >
                      <div style={{
                        width: '22px',
                        height: '22px',
                        backgroundColor: 'white',
                        borderRadius: '50%',
                        position: 'absolute',
                        top: '2px',
                        left: editingTrending ? '26px' : '2px',
                        transition: 'left 0.3s'
                      }} />
                    </div>
                  </div>
                  <small style={{ color: '#999', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Trending items will be highlighted in the menu
                  </small>
                </div>
              </div>

              {/* Offer Toggle */}
              <div className="owner-form-group" style={{ paddingTop: '12px', paddingBottom: '12px', paddingLeft: '16px', paddingRight: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#f9c806', fontSize: '14px', fontWeight: '500' }}>Offer</span>
                  <div 
                    className={`owner-toggle-switch ${editingHasOffer ? 'active' : ''}`}
                    onClick={() => {
                      setEditingHasOffer(!editingHasOffer);
                      if (!editingHasOffer) {
                        // When enabling offer, set default percentage
                        setEditingOfferPercentage(10);
                      }
                    }}
                    style={{
                      width: '50px',
                      height: '26px',
                      backgroundColor: editingHasOffer ? '#fbbf24' : '#4b5563',
                      borderRadius: '13px',
                      position: 'relative',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                  >
                    <div style={{
                      width: '22px',
                      height: '22px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '2px',
                      left: editingHasOffer ? '26px' : '2px',
                      transition: 'left 0.3s'
                    }} />
                  </div>
                </div>
                <small style={{ color: '#999', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                  Enable special offers for this item
                </small>
              </div>
                
              {/* Discount Percentage - Only show when offer is enabled */}
              {editingHasOffer && (
                <div className="owner-form-group" style={{ paddingTop: '12px', paddingBottom: '12px', paddingLeft: '16px', paddingRight: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#fbbf24' }}>
                    Discount Percentage
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="number"
                      max={99}
                      className="owner-form-control"
                      value={editingOfferPercentage}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          setEditingOfferPercentage('');
                        } else {
                          const numVal = parseInt(val);
                          if (!isNaN(numVal) && numVal >= 0 && numVal < 100) {
                            setEditingOfferPercentage(numVal);
                          }
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                    <span style={{ color: '#fbbf24', fontWeight: '600', fontSize: '16px' }}>%</span>
                  </div>
                  <small style={{ color: '#999', fontSize: '11px', marginTop: '8px', display: 'block' }}>
                    Customer will see {editingOfferPercentage || 0}% off
                  </small>
                </div>
              )}

              {/* Description */}
              <div className="owner-form-group">
                <label>Description</label>
                <textarea
                  value={editingDescription}
                  onChange={(e) => setEditingDescription(e.target.value)}
                  rows={3}
                  className="owner-form-control"
                  placeholder="Enter item description"
                />
                <small style={{ color: '#999', fontSize: '12px' }}>
                  Edit the description to provide more details about this item
                </small>
              </div>

              {/* Add Customization Options */}
              <div className="owner-form-group">
                <label>Add New Customization</label>
                
                {/* Show current options if any exist */}
                {(() => {
                  const optionsCustomization = (Array.isArray(selectedItem?.customizations) ? selectedItem.customizations : [])
                    .find(c => c.name === 'Options');
                  
                  if (optionsCustomization && Array.isArray(optionsCustomization.options) && optionsCustomization.options.length > 0) {
                    return (
                      <div className="owner-current-options">
                        <span className="owner-options-label">Current customizations:</span>
                        <div className="owner-options-list">
                          {optionsCustomization.options.map((option, index) => {
                            // Safety check: ensure we're rendering a string, not an object
                            const optionName = typeof option === 'string' ? option : (option?.name || 'Unknown');
                            return (
                              <span key={index} className="owner-option-tag">
                                {optionName}
                                <button 
                                  className="owner-option-remove-btn" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveCustomization(index);
                                  }}
                                  aria-label="Remove customization"
                                >
                                  ✕
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                <div className="owner-add-option-grid">
                  <input
                    className="owner-form-control"
                    placeholder="Enter new customization (e.g., Extra Spicy)"
                    value={newCustName}
                    onChange={e => setNewCustName(e.target.value)}
                  />
                  <button 
                    className="owner-save-btn" 
                    onClick={handleAddCustomization} 
                    disabled={addingCust || !newCustName.trim()}
                  >
                    <Plus size={16} /> {addingCust ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
              
              {/* Enable/Disable Item */}
              <div className="owner-form-group">
                <label>Item Availability</label>
                <button 
                  onClick={() => handleToggleAvailability(selectedItem)} 
                  className={selectedItem.isAvailable ? "owner-disable-btn" : "owner-enable-btn"}
                  disabled={saving || selectedItem.has_low_stock_ingredients === true}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: (saving || selectedItem.has_low_stock_ingredients) ? 'not-allowed' : 'pointer',
                    backgroundColor: selectedItem.has_low_stock_ingredients ? '#6c757d' : (selectedItem.isAvailable ? '#dc3545' : '#28a745'),
                    color: 'white',
                    opacity: (saving || selectedItem.has_low_stock_ingredients) ? 0.6 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  {saving ? 'Updating...' : 
                   selectedItem.has_low_stock_ingredients ? '🔒 System Disabled' :
                   (selectedItem.isAvailable ? 'Disable Item' : 'Enable Item')}
                </button>
                <small style={{ color: '#999', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  {selectedItem.has_low_stock_ingredients 
                    ? `Cannot enable - ${selectedItem.disabled_reason || 'System disabled due to inventory issues'}` :
                   selectedItem.isAvailable 
                    ? 'Disabled items will not be visible to customers' 
                    : 'Enable this item to make it visible to customers'}
                </small>
              </div>
            </div>
            
            <div className="owner-modal-actions">
              <button 
                onClick={handleSaveEdit} 
                className={`owner-save-btn${hasUnsavedCustomizations ? ' unsaved-changes' : ''}`}
                disabled={saving}
                style={hasUnsavedCustomizations ? {
                  animation: 'pulse 2s infinite',
                  boxShadow: '0 0 0 0 rgba(251, 191, 36, 0.7)'
                } : {}}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => {
                setShowEditModal(false);
                setHasUnsavedCustomizations(false);
              }} className="owner-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enable Item Confirmation Modal */}
      {showEnableModal && itemToEnable && (
        <div className="owner-modal-overlay" onClick={() => !saving && setShowEnableModal(false)}>
          <div className="owner-enable-modal" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '80vh', overflow: 'auto', margin: 'auto' }}>
            <div className="owner-modal-header">
              <h3>Enable "{itemToEnable.name}"?</h3>
              <button onClick={() => { 
                setShowEnableModal(false); 
                setIngredientsList([]); 
                setSelectedMissingIngredients({});
                setResolvedIngredients(new Set());
              }} className="owner-close-btn" disabled={saving}>
                <X size={20} />
              </button>
            </div>
            <div className="owner-modal-body">
              {/* Ingredients List */}
              <div style={{ marginBottom: '20px', padding: '0 24px' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>
                    {ingredientsList.some(ing => ing.is_out_of_stock || ing.current_stock === 0)
                      ? 'Out of Stock - ABC Ingredients:' 
                      : 'ABC Ingredients (All Available):'}
                  </span>
                  {(() => {
                    const totalMissing = ingredientsList.filter(ing => ing.is_out_of_stock || ing.current_stock === 0).length;
                    const resolved = resolvedIngredients.size;
                    const remaining = totalMissing - resolved;
                    
                    if (totalMissing > 0 && resolved > 0) {
                      return (
                        <span style={{ 
                          fontSize: '14px', 
                          fontWeight: '600',
                          color: remaining === 0 ? '#10b981' : '#fbbf24',
                          backgroundColor: remaining === 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(251, 191, 36, 0.1)',
                          padding: '4px 12px',
                          borderRadius: '6px',
                          border: remaining === 0 ? '1px solid #10b981' : '1px solid #fbbf24'
                        }}>
                          {remaining === 0 ? '✅ All Resolved!' : `${remaining}/${totalMissing} Remaining`}
                        </span>
                      );
                    }
                    return null;
                  })()}
                </h4>
                {loadingIngredients ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    <div style={{ marginBottom: '8px' }}>⏳ Loading ingredients...</div>
                  </div>
                ) : ingredientsList.length > 0 ? (
                  <div 
                    className="ingredients-scroll-container"
                    style={{ 
                      maxHeight: '300px', 
                      overflowY: 'scroll', 
                      overflowX: 'hidden',
                      border: '2px solid #444', 
                      borderRadius: '8px',
                      backgroundColor: '#27313A',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    {ingredientsList
                      .filter(ing => {
                        const ingredientKey = `${ing.ingredient_name || ing.name}`;
                        const isOutOfStock = ing.is_out_of_stock || ing.current_stock === 0;
                        // Only show ingredients that are out of stock AND not yet resolved
                        return isOutOfStock && !resolvedIngredients.has(ingredientKey);
                      })
                      .map((ing, idx) => {
                      const ingredientKey = `${ing.ingredient_name || ing.name}`;
                      const isOutOfStock = ing.is_out_of_stock || ing.current_stock === 0;
                      
                      return (
                        <div 
                          key={idx} 
                          style={{ 
                            padding: '12px 16px', 
                            borderBottom: idx < ingredientsList.length - 1 ? '1px solid #333' : 'none',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: isOutOfStock ? '#222' : '#111518',
                            borderLeft: 'none'
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontWeight: '500', 
                              color: '#ffffff', 
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              {ing.ingredient_name || ing.name}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%', marginBottom: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                {ing.current_stock !== undefined && (
                                  <span style={{
                                    fontSize: '11px',
                                    color: isOutOfStock ? 'rgb(236, 17, 17)' : '#999',
                                    fontWeight: '500'
                                  }}>
                                    Available: {parseFloat(ing.current_stock).toFixed(2)} {ing.unit_name || ing.unit}
                                  </span>
                                )}
                              </div>
                              <span style={{
                                fontSize: '11px',
                                color: '#ffffff',
                                fontWeight: '500'
                              }}>
                                Need: {ing.quantity} {ing.unit_name || ing.unit}
                              </span>
                            </div>
                          </div>
                          
                          {/* Checkbox for missing ingredients only */}
                          {isOutOfStock && (
                            <div style={{ marginLeft: '12px' }}>
                              <input
                                type="checkbox"
                                checked={Boolean(selectedMissingIngredients[ingredientKey])}
                                onChange={() => {
                                  setSelectedMissingIngredients(prev => ({
                                    ...prev,
                                    [ingredientKey]: !prev[ingredientKey]
                                  }));
                                }}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'pointer',
                                  accentColor: '#fbbf24'
                                }}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', backgroundColor: '#111518', borderRadius: '8px' }}>
                    <div>No ingredients found for this item</div>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              {ingredientsList.some(ing => ing.is_out_of_stock || ing.current_stock === 0) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 24px' }}>
                  {/* Three main action buttons matching TSR modal */}
                  <button 
                    onClick={() => {
                      const selectedCount = Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]).length;
                      if (selectedCount === 0) {
                        alert('Please select at least one ingredient using the checkboxes');
                        return;
                      }
                      
                      // Get selected ingredients
                      const selectedKeys = Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]);
                      
                      // Calculate remaining unresolved ingredients
                      const allResolvedKeys = new Set([...resolvedIngredients, ...selectedKeys]);
                      const remainingIngredients = ingredientsList.filter(ing => {
                        const key = ing.ingredient_name || ing.name;
                        const isOutOfStock = ing.is_out_of_stock || ing.current_stock === 0;
                        return isOutOfStock && !allResolvedKeys.has(key);
                      });
                      
                      // Check if all missing ingredients are now resolved
                      if (remainingIngredients.length === 0) {
                        // All ingredients resolved - enable the item
                        const confirmed = window.confirm(
                          `All missing ingredients have been handled!\n\n` +
                          `${selectedCount} ingredient(s) will go into negative stock.\n` +
                          `"${itemToEnable.name}" will now be enabled.\n\n` +
                          `Continue?`
                        );
                        
                        if (confirmed) {
                          // Call confirmEnableItem which will handle clearing state after success
                          confirmEnableItem();
                        }
                      } else {
                        // Still have unresolved ingredients - mark selected as resolved but keep modal open
                        setResolvedIngredients(prev => {
                          const newSet = new Set(prev);
                          selectedKeys.forEach(key => newSet.add(key));
                          return newSet;
                        });
                        
                        // Clear selections for resolved ingredients only
                        const newSelections = { ...selectedMissingIngredients };
                        selectedKeys.forEach(key => delete newSelections[key]);
                        setSelectedMissingIngredients(newSelections);
                        
                        alert(
                          `✅ ${selectedCount} ingredient(s) marked for negative stock.\n\n` +
                          `⚠️ ${remainingIngredients.length} ingredient(s) still need to be handled.\n\n` +
                          `Please select the remaining ingredients and choose an option.`
                        );
                      }
                    }}
                    className="owner-save-btn"
                    disabled={saving || Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]).length === 0}
                    style={{ 
                      width: '100%',
                      padding: '14px 24px',
                      backgroundColor: '#fbbf24',
                      border: '2px solid #f59e0b',
                      color: '#000',
                      fontWeight: '700',
                      fontSize: '15px',
                      opacity: (saving || Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]).length === 0) ? 0.5 : 1
                    }}
                    title="Enable item and allow selected ingredients to go negative"
                  >
                    {saving ? 'Enabling...' : 'Confirm & Create Negative Stock'}
                  </button>
                  

                  
                  <button 
                    onClick={() => {
                      const selectedCount = Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]).length;
                      if (selectedCount === 0) {
                        alert('Please select at least one ingredient using the checkboxes');
                        return;
                      }
                      
                      // Store selected ingredients to be resolved AFTER recipe is saved
                      const selectedKeys = Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]);
                      
                      // Calculate what would remain after these ingredients are handled
                      const potentiallyResolvedKeys = new Set([...resolvedIngredients, ...selectedKeys]);
                      const remainingIngredients = ingredientsList.filter(ing => {
                        const key = ing.ingredient_name || ing.name;
                        const isOutOfStock = ing.is_out_of_stock || ing.current_stock === 0;
                        return isOutOfStock && !potentiallyResolvedKeys.has(key);
                      });
                      
                      // Always navigate to Item Mapping to update recipe
                      const confirmed = window.confirm(
                        `Navigate to Item Mapping?\n\n` +
                        `Selected: ${selectedCount} ingredient(s) will be removed from "${itemToEnable.name}" recipe.\n` +
                        (remainingIngredients.length > 0 
                          ? `\n⚠️ Note: ${remainingIngredients.length} other ingredient(s) still need handling after recipe update.\n`
                          : `\n✅ All missing ingredients will be resolved after this.\n`) +
                        `\nContinue?`
                      );
                      
                      if (confirmed) {
                        
                        // Store context for Item Mapping
                        const menuItemId = itemToEnable.menu_item_id || itemToEnable.id;
                        const itemName = itemToEnable.name;
                        
                        localStorage.setItem('autoSelectRecipeId', menuItemId);
                        localStorage.setItem('autoSelectRecipeName', itemName); // Fallback for name matching
                        localStorage.setItem('cameFromMenuManagement', 'true'); // Flag for Item Mapping
                        localStorage.setItem('pendingMenuManagementData', JSON.stringify({
                          menuItemId: menuItemId,
                          itemName: itemToEnable.name,
                          selectedIngredients: selectedKeys,
                          ingredientsToRemove: selectedKeys, // Ingredients to remove from recipe
                          pendingResolve: selectedKeys, // Mark these as resolved AFTER save
                          action: 'removeIngredients',
                          resolvedIngredients: Array.from(resolvedIngredients), // Currently resolved (not including new ones)
                          hasRemainingIngredients: remainingIngredients.length > 0
                        }));
                        
                        
                        // Close the modal but keep resolved ingredients
                        setShowEnableModal(false);
                        setItemToEnable(null);
                        setIngredientsList([]);
                        setSelectedMissingIngredients({});
                        // DON'T clear resolvedIngredients - we need them when returning
                        
                        // Navigate to Item Mapping (same as TSR)
                        if (goTo) {
                          goTo('itemmap'); // Changed from 'qr-itemmap' to 'itemmap'
                        } else {
                          window.location.hash = '/itemmap';
                        }
                        
                      }
                    }}
                    className="owner-save-btn"
                    disabled={saving || Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]).length === 0}
                    style={{ 
                      width: '100%',
                      padding: '14px 24px',
                      backgroundColor: '#ef4444',
                      border: '2px solid #dc2626',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: '15px',
                      opacity: (saving || Object.keys(selectedMissingIngredients).filter(k => selectedMissingIngredients[k]).length === 0) ? 0.5 : 1
                    }}
                    title="Remove selected ingredients and update recipe in Item Mapping"
                  >
                    Remove and Update Recipe
                  </button>
                  
                  {/* Enable Item button when all resolved */}
                  {(() => {
                    const totalMissing = ingredientsList.filter(ing => ing.is_out_of_stock || ing.current_stock === 0).length;
                    const remaining = totalMissing - resolvedIngredients.size;
                    
                    if (totalMissing > 0 && remaining === 0) {
                      return (
                        <button 
                          onClick={async () => {
                            const confirmed = window.confirm(
                              `All missing ingredients have been handled!\n\n` +
                              `"${itemToEnable.name}" will now be enabled.\n\n` +
                              `Continue?`
                            );
                            
                            if (confirmed) {
                              try {
                                setSaving(true);
                                const menuItemId = itemToEnable.menu_item_id || itemToEnable.id;
                                const response = await fetch(`${API_BASE_URL}/qr-billing/menu/${menuItemId}/availability`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ is_available: true })
                                });
                                
                                if (response.ok) {
                                  showSuccess(`✅ "${itemToEnable.name}" is now enabled!`);
                                  setShowEnableModal(false);
                                  setItemToEnable(null);
                                  setIngredientsList([]);
                                  setResolvedIngredients(new Set());
                                  await loadMenuItems();
                                } else {
                                  throw new Error('Failed to enable item');
                                }
                              } catch (err) {
                                console.error('Error enabling item:', err);
                                setError('Failed to enable item');
                              } finally {
                                setSaving(false);
                              }
                            }
                          }}
                          className="owner-save-btn"
                          disabled={saving}
                          style={{ 
                            width: '100%',
                            padding: '14px 24px',
                            backgroundColor: '#10b981',
                            border: '2px solid #059669',
                            color: '#fff',
                            fontWeight: '700',
                            fontSize: '15px',
                            marginBottom: '12px',
                            opacity: saving ? 0.5 : 1
                          }}
                          title="All ingredients resolved - enable this item"
                        >
                          ✅ Enable Item Now
                        </button>
                      );
                    }
                    return null;
                  })()}
                </div>
              ) : (
                // No missing ingredients - show simple Enable/Cancel buttons
                <div className="owner-modal-actions" style={{ gap: '12px', padding: '0 24px' }}>
                  <button 
                    onClick={() => { 
                      setShowEnableModal(false); 
                      setIngredientsList([]); 
                      setSelectedMissingIngredients({});
                    }} 
                    className="owner-cancel-btn"
                    disabled={saving}
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  
                  <button 
                    onClick={confirmEnableItem} 
                    className="owner-save-btn" 
                    disabled={saving}
                    style={{ 
                      flex: 1,
                      opacity: saving ? 0.5 : 1,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      backgroundColor: '#22C55E',
                      border: '1px solid #4ADE80'
                    }}
                    title="Enable this item"
                  >
                    {saving ? 'Enabling...' : 'Enable'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '24px 32px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          animation: 'successPopup 0.3s ease-out'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '4px' }}>
              Success!
            </div>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>
              Menu item updated successfully
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Trending/Offer Changes */}
      {showConfirmationModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#2a2a2a',
            borderRadius: '20px',
            padding: '40px 36px',
            maxWidth: '520px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
            textAlign: 'center'
          }}>
            {/* Warning Icon */}
            <div style={{
              width: '70px',
              height: '70px',
              margin: '0 auto 24px',
              borderRadius: '50%',
              border: '3px solid #F9C806',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F9C806" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>

            {/* Title */}
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '24px',
              fontWeight: '700',
              color: '#ffffff'
            }}>
              Confirm Changes
            </h3>

            {/* Message */}
            <div style={{
              padding: '24px 28px',
              backgroundColor: 'rgba(0, 0, 0, 0.25)',
              borderRadius: '12px',
              marginBottom: '32px',
              textAlign: 'left'
            }}>
              <div style={{
                color: '#e0e0e0',
                fontSize: '15px',
                lineHeight: '1.8',
                whiteSpace: 'pre-line'
              }}>
                {confirmationMessage}
              </div>
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCancelSave}
                disabled={saving}
                style={{
                  padding: '14px 32px',
                  borderRadius: '10px',
                  border: '2px solid #555',
                  backgroundColor: 'transparent',
                  color: '#999',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: saving ? 0.5 : 1,
                  minWidth: '140px'
                }}
                onMouseOver={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    e.currentTarget.style.borderColor = '#777';
                    e.currentTarget.style.color = '#ccc';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#555';
                  e.currentTarget.style.color = '#999';
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={saving}
                style={{
                  padding: '14px 32px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: saving ? '#b8902f' : '#F9C806',
                  color: '#1a1a1a',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  minWidth: '180px'
                }}
                onMouseOver={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#ffcc00ff';
                  }
                }}
                onMouseOut={(e) => {
                  if (!saving) {
                    e.currentTarget.style.backgroundColor = '#F9C806';
                  }
                }}
              >
                {saving ? (
                  <>
                    <span style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(26, 26, 26, 0.3)',
                      borderTopColor: '#1a1a1a',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }}></span>
                    Saving...
                  </>
                ) : (
                  'Confirm'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      )}
    </div>
  );
};

export default OwnerMenuManagement;
